import {
  IconAlertTriangle,
  IconArrowLeft,
  IconChevronDown,
  IconChevronUp,
  IconKeyboard,
  IconMicrophone,
  IconNote,
  IconPencil,
  IconPhone,
  IconPlayerStopFilled,
  IconSignature,
} from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, useToast } from '@/components/common';
import { TopbarTitle } from '@/components/layout';
import { useStoredState } from '@/hooks';
import { LANGUAGE_NAME, type Language } from '@/i18n';
import { errorMessage } from '@/lib/api-client';
import { navigate } from '@/lib/router';
import type { Consent, Patient } from '@/types/api';
import { allergyLabel } from '@/utils/allergies';
import { missingProtocolFields } from '@/utils/patient';
import { consentsApi } from '../../consents/api';
import { patientsApi } from '../../patients/api';
import { PatientForm } from '../../patients/PatientForm';
import { formatDate } from './format';
import { HealthSheet } from './HealthSheet';

/** Back button and name, in the top bar right of the logo, so the chart starts higher. */
export function PatientHeader({ patient: p }: { patient: Patient }) {
  const { t } = useTranslation();
  return (
    <TopbarTitle>
      <button
        type="button"
        className="icon-btn"
        onClick={() => navigate({ name: 'patients' })}
        aria-label={t('chart.header.back')}
      >
        <IconArrowLeft size={24} />
      </button>
      <h1>
        {p.lastName} {p.firstName}
      </h1>
    </TopbarTitle>
  );
}

/** Left column: allergies and diseases first, then notes and the contact details. */
export function PatientInfo({
  patient: p,
  onSaved,
}: {
  patient: Patient;
  onSaved: (patient: Patient) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const missing = missingProtocolFields(p);
  return (
    <section className="card patient-info">
      <div className="patient-info__health">
        <div className="row">
          <span className="field__label grow">{t('chart.health.title')}</span>
          <button
            type="button"
            className="icon-btn icon-btn--sm"
            onClick={() => setEditing(true)}
            aria-label={t('chart.health.edit')}
            title={t('chart.health.edit')}
          >
            <IconPencil size={16} />
          </button>
        </div>
        {p.allergies.length > 0 && (
          <div className="patient-info__allergies" role="note">
            <IconAlertTriangle size={18} aria-hidden="true" />
            <div>
              <strong>{t('chart.health.allergies')}</strong>
              <p>{p.allergies.map(allergyLabel).join(', ')}</p>
            </div>
          </div>
        )}
        {p.diagnoses.length > 0 && (
          <ul className="icd-list icd-list--compact">
            {p.diagnoses.map((d) => (
              <li key={d.code} className="icd-list__item">
                <span className="icd-code">{d.code}</span>
                <span>{d.name}</span>
              </li>
            ))}
          </ul>
        )}
        {!(p.allergies.length || p.diagnoses.length) && (
          <p className="small muted">{t('chart.health.none')}</p>
        )}
      </div>
      {p.notes && (
        <div className="patient-info__alert">
          <IconNote size={18} aria-hidden="true" />
          <div>
            <div className="field__label">{t('chart.info.notes')}</div>
            <p>{p.notes}</p>
          </div>
        </div>
      )}
      {missing.length > 0 && (
        <div className="patient-info__missing" role="note">
          <IconAlertTriangle size={18} aria-hidden="true" />
          <div className="grow">
            <strong>{t('chart.info.missingTitle')}</strong>
            <p className="small">
              {missing.map((field) => t(`patients.form.${field}`)).join(', ')}
            </p>
          </div>
          <button type="button" className="btn btn--sm" onClick={() => setEditingDetails(true)}>
            {t('chart.info.complete')}
          </button>
        </div>
      )}
      <div className="row">
        <span className="field__label grow">{t('chart.info.details')}</span>
        <button
          type="button"
          className="icon-btn icon-btn--sm"
          onClick={() => setEditingDetails(true)}
          aria-label={t('chart.info.editDetails')}
          title={t('chart.info.editDetails')}
        >
          <IconPencil size={16} />
        </button>
      </div>
      <PatientFacts patient={p}>
        <PatientConsents patientId={p.id} />
      </PatientFacts>
      {editing && <HealthSheet patient={p} onClose={() => setEditing(false)} onSaved={onSaved} />}
      {editingDetails && (
        <DetailsSheet patient={p} onClose={() => setEditingDetails(false)} onSaved={onSaved} />
      )}
    </section>
  );
}

/**
 * The personal details: the birth date and sex always; the rest, and
 * `children` (the consents), behind "Show all" (kept per device), so the
 * column stays short on a phone.
 */
function PatientFacts({ patient: p, children }: { patient: Patient; children: ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useStoredState('dentlog.chart.details', false);
  const born = p.birthDate ? formatDate(p.birthDate) : t('chart.header.noBirthDate');
  return (
    <div className="patient-info__facts">
      <dl className="facts">
        <dt>{t('chart.info.born')}</dt>
        <dd>
          {born}
          {p.sex && ` · ${t(`patients.sex.${p.sex}`)}`}
        </dd>
      </dl>
      {open && (
        <dl className="facts" id="patient-facts-more">
          {p.parentName && (
            <>
              <dt>{t('patients.form.parentName')}</dt>
              <dd>{p.parentName}</dd>
            </>
          )}
          {p.jmbg && (
            <>
              <dt>{t('patients.form.jmbg')}</dt>
              <dd>{p.jmbg}</dd>
            </>
          )}
          {p.lbo && (
            <>
              <dt>{t('patients.form.lbo')}</dt>
              <dd>{p.lbo}</dd>
            </>
          )}
          {p.address && (
            <>
              <dt>{t('patients.form.address')}</dt>
              <dd>{p.address}</dd>
            </>
          )}
          <dt>{t('chart.info.phone')}</dt>
          <dd>
            {p.phone ? (
              <a href={`tel:${p.phone.replace(/\s+/g, '')}`} className="patient-info__phone">
                <IconPhone size={16} aria-hidden="true" /> {p.phone}
              </a>
            ) : (
              '—'
            )}
          </dd>
        </dl>
      )}
      {open && children}
      <button
        type="button"
        className="btn btn--sm patient-info__more"
        aria-expanded={open}
        aria-controls={open ? 'patient-facts-more' : undefined}
        onClick={() => setOpen(!open)}
      >
        {open ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
        {t(open ? 'chart.info.showLess' : 'chart.info.showAll')}
      </button>
    </div>
  );
}

/** The patient's consents to invasive procedures (#23), and a new one. */
function PatientConsents({ patientId }: { patientId: string }) {
  const { t } = useTranslation();
  const [consents, setConsents] = useState<Consent[] | null>(null);
  useEffect(() => {
    let live = true;
    consentsApi
      .list({ patientId, limit: 20 })
      .then((list) => live && setConsents(list))
      .catch(() => live && setConsents(null));
    return () => {
      live = false;
    };
  }, [patientId]);
  return (
    <div className="patient-info__consents">
      <div className="row">
        <span className="field__label grow">{t('consents.title')}</span>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => navigate({ name: 'consent', id: 'new', patientId })}
        >
          <IconSignature size={16} /> {t('consents.new')}
        </button>
      </div>
      {consents && consents.length > 0 && (
        <ul className="patient-info__consent-list">
          {consents.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="link"
                onClick={() => navigate({ name: 'consent', id: c.id })}
              >
                {c.number} · {c.methodName}
              </button>
              <span className="small muted"> · {formatDate(c.issuedAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The patient's personal details (the protocol's), edited with the new-patient form. */
function DetailsSheet({
  patient,
  onClose,
  onSaved,
}: {
  patient: Patient;
  onClose: () => void;
  onSaved: (patient: Patient) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <Sheet title={t('chart.info.details')} onClose={onClose}>
      <PatientForm
        initial={patient}
        busy={busy}
        submitLabel={t('common.save')}
        onSubmit={async (input) => {
          setBusy(true);
          try {
            onSaved(await patientsApi.update(patient.id, input));
            onClose();
          } catch (err) {
            toast.show(errorMessage(err), 'error');
          } finally {
            setBusy(false);
          }
        }}
      />
    </Sheet>
  );
}

type FabProps = {
  listening: boolean;
  supported: boolean;
  typing: boolean;
  /** Viewing an earlier visit: nothing can be recorded. */
  locked?: boolean;
  onMic: () => void;
  onTyping: () => void;
  /** Dictation language; tapping the chip switches to the next one. */
  language: Language;
  onLanguage: () => void;
};

function micLabel(p: { listening: boolean; supported: boolean }, t: TFunction): string {
  if (!p.supported) return t('chart.dock.keyboardDictation');
  return p.listening ? t('chart.dock.stop') : t('chart.dock.start');
}

/** Floating controls in the bottom right: typing and the microphone. */
export function ChartFab(p: FabProps) {
  const { t } = useTranslation();
  return (
    <div className="fab" role="toolbar" aria-label={t('chart.dock.label')}>
      <button
        type="button"
        className="fab__btn"
        aria-pressed={p.typing}
        onClick={p.onTyping}
        disabled={p.locked}
        aria-label={t('chart.dock.typing')}
        title={t('chart.dock.typing')}
      >
        <IconKeyboard size={24} />
      </button>
      <div className="mic-wrap">
        <button
          type="button"
          className={`mic ${p.listening ? 'mic--live' : ''}`}
          onClick={p.onMic}
          disabled={p.locked}
          aria-pressed={p.listening}
          aria-label={micLabel(p, t)}
        >
          {p.listening ? <IconPlayerStopFilled size={30} /> : <IconMicrophone size={32} />}
        </button>
        <button
          type="button"
          className="mic-wrap__lang"
          onClick={p.onLanguage}
          disabled={p.locked || p.listening}
          aria-label={t('chart.dock.dictationLanguage', { lang: LANGUAGE_NAME[p.language] })}
          title={t('chart.dock.dictationLanguage', { lang: LANGUAGE_NAME[p.language] })}
        >
          {p.language.toUpperCase()}
        </button>
      </div>
    </div>
  );
}
